'use client';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type Dictionary,
  Notification,
  UnauthorizedError,
} from '@flemingmetrics/api-client';
import { DIALOGS } from '@/constants/dialogs';
import { showErrorToaster } from '@/utils/toasters';
import { filterByValues, mergeWithoutDuplicates } from '@/utils/arrays';
import { useSockets } from '@/hooks/useSockets';
import { usePath } from '@/hooks/usePath';
import { COMPANY_TABS } from '@/constants/companyTabs';
import { getCompanyUrl } from '@/api/getUrl';
import {
  DEFAULT_DIALOGS,
  isFileNotification,
  isFinancialReportNotification,
  isPostNotification,
  updateReadNotification,
} from './utils';

type AppStateValue = {
  recentCompanyIds: number[];
  recentInvestorIds: number[];
  activeDialogs: Dictionary<boolean>;
  setActiveDialogs: Dispatch<SetStateAction<{ [p: string]: boolean }>>;
  onOpenDialog: (dialogName: string) => void;
  onCloseDialog: (dialogName: string) => void;
  onAddRecentCompanyId: (companyId: number) => void;
  onAddRecentInvestorId: (investorId: number) => void;
  onRemoveRecentCompanyIds: (companyIds?: number[]) => void;
  onRemoveRecentInvestorIds: (investorIds?: number[]) => void;
  onOpenNotification: (notification: Notification) => void;
  onToggleUnloadWarning: (activate: boolean) => void;
  onLogin: () => void;
  onApiError: (err: any, message: string, cb?: Function) => void;
};

export const AppStateContext = createContext<AppStateValue>({
  recentCompanyIds: [],
  recentInvestorIds: [],
  activeDialogs: DEFAULT_DIALOGS,
  setActiveDialogs: () => null,
  onOpenDialog: () => null,
  onCloseDialog: () => null,
  onAddRecentCompanyId: () => null,
  onAddRecentInvestorId: () => null,
  onRemoveRecentCompanyIds: () => null,
  onRemoveRecentInvestorIds: () => null,
  onOpenNotification: () => null,
  onToggleUnloadWarning: () => null,
  onLogin: () => null,
  onApiError: () => null,
});

export default function AppStateProvider({ children }: PropsWithChildren) {
  const loginCallbacks = useRef<Function[]>([]);
  const { setApiErrorCallback } = useSockets();
  const { navigate, pushParams, removeParams } = usePath();
  const [recentCompanyIds, setRecentCompanyIds] = useState<number[]>([]);
  const [recentInvestorIds, setRecentInvestorIds] = useState<number[]>([]);
  const [activeDialogs, setActiveDialogs] = useState(DEFAULT_DIALOGS);

  useEffect(() => {
    setApiErrorCallback(handleSocketError);
  }, []);

  const handleOpenDialog = (dialogName: string) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: true }));
  };

  const handleCloseDialog = (dialogName: string) => {
    setActiveDialogs((dialogs) => ({ ...dialogs, [dialogName]: false }));
  };

  const handleSocketError = () => {
    handleApiError(new UnauthorizedError('', ''), '');
  };

  const handleApiError = (err: any, toasterMessage: string, cb?: Function) => {
    if (err instanceof UnauthorizedError) {
      if (cb) loginCallbacks.current.push(cb);
      if (!activeDialogs[DIALOGS.LOGIN]) handleOpenDialog('login');
    } else {
      showErrorToaster(toasterMessage);
      throw err;
    }
  };

  const handleLogin = () => {
    if (loginCallbacks.current) {
      loginCallbacks.current.forEach((cb) => cb());
    }
    handleCloseDialog(DIALOGS.LOGIN);
    loginCallbacks.current = [];
  };

  const handleAddRecentCompanyId = (companyId: number) => {
    setRecentCompanyIds((ids) =>
      mergeWithoutDuplicates([companyId], ids).slice(0, 5)
    );
  };

  const handleAddRecentInvestorId = (investorId: number) => {
    setRecentInvestorIds((ids) =>
      mergeWithoutDuplicates([investorId], ids).slice(0, 5)
    );
  };

  const handleRemoveRecentCompanyIds = (companyIds?: number[]) => {
    if (companyIds === undefined) setRecentCompanyIds([]);
    else setRecentCompanyIds((ids) => filterByValues(ids, companyIds));
  };

  const handleRemoveRecentInvestorIds = (investorIds?: number[]) => {
    if (investorIds === undefined) setRecentInvestorIds([]);
    else setRecentInvestorIds((ids) => filterByValues(ids, investorIds));
  };

  const handleToggleUnloadWarning = (activate: boolean) => {
    if (activate) handleActivateUnloadWarning();
    else handleDeactivateUnloadWarning();
  };

  const handleActivateUnloadWarning = () => {
    window.removeEventListener('beforeunload', handleUnloadWarning); // Prevent duplicate listeners
    window.addEventListener('beforeunload', handleUnloadWarning);
  };

  const handleDeactivateUnloadWarning = () => {
    window.removeEventListener('beforeunload', handleUnloadWarning);
  };

  const handleUnloadWarning = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  const handleOpenNotification = async (notification: Notification) => {
    // No need to wait on this, the socket event will handle it
    if (!notification.isRead) updateReadNotification(notification.id);
    let params: Dictionary<string> = {};
    handleAddRecentCompanyId(notification.companyId);
    // Determine what to open / navigate to
    if (isPostNotification(notification) || isFileNotification(notification)) {
      params.companyTab = COMPANY_TABS.NEWS;
    } else if (isFinancialReportNotification(notification)) {
      params.companyTab = COMPANY_TABS.INVESTORS;
    } else {
      showErrorToaster('Error opening notification');
      throw Error(
        // @ts-ignore -- NOTE: Means there's a notificationType that isn't in the client-api yet
        `Unrecognized notificationTypeId: ${notification.notificationTypeId}`
      );
    }
    navigate(getCompanyUrl(notification.companyId), params);
  };

  const contextValue = {
    recentCompanyIds,
    recentInvestorIds,
    activeDialogs,
    setActiveDialogs,
    onOpenDialog: handleOpenDialog,
    onCloseDialog: handleCloseDialog,
    onAddRecentCompanyId: handleAddRecentCompanyId,
    onAddRecentInvestorId: handleAddRecentInvestorId,
    onRemoveRecentCompanyIds: handleRemoveRecentCompanyIds,
    onRemoveRecentInvestorIds: handleRemoveRecentInvestorIds,
    onOpenNotification: handleOpenNotification,
    onToggleUnloadWarning: handleToggleUnloadWarning,
    onLogin: handleLogin,
    onApiError: handleApiError,
  };

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
}
